import React, { useState } from 'react';
import { emails, urls } from '../../constants';
import PropTypes from 'prop-types';
import { errors } from '../../constants';
import { helpCuf } from '../../api/cuf';
import { withRouter } from '../WithRouter';
import { Phone, Mail, X, CircleCheck } from 'lucide-react';
import { Box, Button, Card, CardContent, InfoPanel, Label, TextField, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const HelpSection = ({ params: { symbol }, companyName, userEmail, userFullName }) => {
  const [showReportProblem, setReportProblem] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [problemText, setProblemText] = useState('');

  const handleReportProblemClick = () => {
    setSubmitSuccess(false);
    setSubmitError(false);
    setReportProblem(true);
  };

  const handleSendReportProblem = () => {
    const body = {
      senderEmail: userEmail,
      fullName: userFullName,
      companyName: companyName,
      symbol: symbol,
      textBox: problemText
    };

    setIsSending(true);

    helpCuf(body)
      .then(() => {
        setIsSending(false);
        setSubmitSuccess(true);
        setReportProblem(false);
        setProblemText('');
      })
      .catch(e => {
        console.error(e);
        setIsSending(false);
        setSubmitError(errors.FAILED_REPORT_PROBLEM);
      });
  };

  const onProblemTextChanged = e => {
    setProblemText(e.target.value);
  };

  return <Box component='section' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant='h3'>Saving Progress</Typography>
    <Typography variant='body1'>
      You can save your work at any time and resume later by clicking on the Save button on the top right corner of your screen. Your progress is also automatically saved as you move between different sections of the application form.
    </Typography>
    <Typography variant='h3' className='mtXL'>Helpful Links</Typography>
    <Box component='ul' sx={{ paddingLeft: "18px" }}>
      <li>
        <a href={urls.PRIVACY_POLICY} target='_blank' rel='noopener noreferrer'>
          Privacy Policy
        </a>
      </li>
      <li>
        <a href={urls.STOCK_PROMO_POLICY} target='_blank' rel='noopener noreferrer'>
          Stock Promotion Policy
        </a>
      </li>
      <li>
        <a href={urls.BEST_PRACTICE_ISSUERS} target='_blank' rel='noopener noreferrer'>
          Best Practices for Issuers
        </a>
      </li>
      <li>
        <a href={urls.GLOSSARY} target='_blank' rel='noopener noreferrer'>
          Glossary of Terms
        </a>
      </li>
      <li>
        <a href={urls.FAQS} target='_blank' rel='noopener noreferrer'>
          FAQs
        </a>
      </li>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 2 }}>
      <Button onClick={handleReportProblemClick}>
        Report a Problem
      </Button>
      {submitSuccess && <Card hasBorder type="highlight">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Label startIcon={<CircleCheck size={16} color="var(--otc-green)" />}>
              Problem has been successfully submitted.
            </Label>
            <Box sx={{ cursor: 'pointer' }}>
              <X size={16} onClick={() => setSubmitSuccess(false)} />
            </Box>
          </Box>
        </CardContent>
      </Card>}
      {showReportProblem && <Card hasBorder>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant='h3'>Report a Problem</Typography>
            <Typography variant='body1'>
              Provide a quick summary of the problem you are experiencing while completing your Company Profile and an analyst from Issuer Services will be in touch. Please make sure to leave your preferred contact details below.
            </Typography>
            <TextField
              multiline
              value={problemText}
              onChange={onProblemTextChanged}
              placeholder='Describe the problem you are experiencing.'
              maxLength={1500}
              showCharCounter
            />
            <Typography variant='body1'>
              To reach Issuer Services immediately, please refer to the contact details below.
            </Typography>
            {submitError && <InfoPanel isWarning title={submitError} />}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant='text' onClick={() => setReportProblem(false)}>
                Cancel
              </Button>
              <Button
                variant='text'
                onClick={handleSendReportProblem}
                isLoading={isSending}
                disabled={(!problemText || problemText.trim().length === 0) || isSending}>
                Submit
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>}
    </Box>
    <Typography variant='h3' className='mtXL'>Contact Issuer Services</Typography>
    <Typography variant='h4' className='mtLg'>Phone:</Typography>
    <Box sx={{ color: "var(--otc-green)" }}>
      <Phone size={20} /> (212)-896-4420
    </Box>
    <Typography variant='h4' className='mtLg'>Email:</Typography>
    <a href={emails.ISSUER_SERVICES}>
      <Mail size={20} /> issuers@otcmarkets.com
    </a>
  </Box>;
};

HelpSection.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string
  }),
  companyName: PropTypes.string,
  userEmail: PropTypes.string,
  userFullName: PropTypes.string
};

export default withRouter(HelpSection);
